import { IAssignEntity } from "../basics/basics.models";
import { UnitOfWorkItem } from "../../utilities/unit-of-work-observer";
import { DataType, DataGridColumn } from "../queries/queries.models";
import { SubtaskCorrespondenceEmailModel } from "../vetting/vetting.models";
import { BaseValidationResponse, ValidationResult } from "../validations/validations.models";
import * as moment from "moment";
import { TasksConstants } from "../tasks/task.model";
import { HistoryHelper } from "../../helpers/history-helper";
import { RightshipDataGridColumn } from "../../views/components/rightship-data-grid/rightship-data-grid";
import { AnonVesselDetailModel } from "../vessels/vessel.models";

export interface GhgVerificationModel extends IAssignEntity {
    ghgVerificationId: number;
    vesselId: number;
    hasEvdiTechnicalFile: boolean;
    hasIceClassNotation: boolean;
    iceClassIndicatorId: number;
    builtToCSR: boolean;
    dpClass: boolean;
    displacement: number;
    lengthBetweenPerpendiculars: number;
    draught: number;
    breadthMoulded: number;
    deadWeightTonnage: number;
    lightDisplacementTonnage: number;
    grossTonnage: number;
    cargoTankCubicCapacity: number;
    twentyFootEquivalentUnitsCapacity: number;
    hasCranes: boolean;
    noOfCranes: number;
    craneSWL: number;
    craneReach: number;
    hasRoRoRamp: boolean;
    roroWeight: number;
    hasSideloaderRamp: boolean;
    sideloaderWeight: number;
    numberOfMainEngines: number;
    powerKwMax: number;
    mainSfc: number;
    auxSfc: number;
    mainPilotSfc: number;
    auxPilotSfc: number;
    shopTestFuelType1Id: number;
    shopTestFuelType2Id: number;
    pilotFuelType1Id: number;
    pilotFuelType2Id: number;
    numberOfAuxiliaryEngines: number;
    totalPowerOfAuxiliaryEngines: number;
    pae: number;
    hasShaftGenerator: boolean;
    powerOfShaftGenerator: number;
    hasShaftMotor: boolean;
    pPTI: number;
    steamTurbineBoilerSfc: number;
    isElectricPowerSuppliedByTurbineGenerator: boolean;
    hasReliquefactionSystem: boolean;
    boilOffGasRatePerDay: number;
    boilOffGasToBeReliquefied: number;
    totalBoilOffGas: number;
    isCopCoolingComputed: boolean;
    copCooling: number;
    compressorsForHighPressuredGas: boolean;
    isCOPcompComputed: boolean;
    copComp: number;
    compressorsForLowPressuredGas: boolean;
    mPPmotor: number;
    speed: number;
    eedi: number;
    verifyEnergySavingEquipment: boolean;
    verifyGhg: boolean;
    verifyEexi: boolean;
    verifyCii: boolean;
    comments: string;
    ghgVerificationEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[];
    ghgVerificationEexi: GhgVerificationEexiModel;
    ghgVerificationCii: GhgVerificationCiiModel;
    ghgVerificationEvdiStatusId: number;
    isClosed: boolean;
    guid?: string;
    currentGhgVerificationStatusHistory?: GhgVerificationStatusHistory;
    isCustomerDataAgreementAccepted: boolean;
    ballastDraft: number;
    totalPowerOfMainEngines: number;
    canRequestEexi: boolean;
    hasOriginalSpeedPowerCurveUpdated?: boolean;
    speedWithoutPaintEffect?: number;
    paintTypeId?: number;
}

export interface GhgVerificationRequestModel {
    ghgVerification: GhgVerificationModel;
    vessel: AnonVesselDetailModel;
    correspondenceEmails: GhgVerificationCorrespondenceEmail[];
    verifyOptions: { id: number, name: string, isSelected: boolean }[];
    evdiFiles: File[];
    capacityPlanFiles: File[];
    currentClassCertificateFiles: File[];
    mainEngineShopTestFiles: File[];
    auxiliaryEngineShopTestFiles: File[];
    seaTrialReportFiles: File[];
    certifiedDocumentsFiles: File[];
    reliquefactionDocumentationsFiles: File[];
    energySavingEquipmentFiles: File[];
    eexiFiles: File[];
    eplShaPoLiFiles: File[];
    eeCertificateFiles: File[];
    ciiFiles: File[];
    ciiCorrectiveActionPlanFiles: File[];
    navigateToEexi: () => Promise<void>;
    toggleEnergySavingEquipment: (isSelected: boolean) => void;
    isExternalEnvironmentalVerification: boolean;
}

export interface GhgVerificationStatusHistory {
    ghgVerificationStatusId: number;
}

export interface BaseGhgVerificationRatingFactorClientModel {
    ghgVerificationId: number;
    displayName: string;
    group: string;
    propertyName: string;
    ihsValue: string;
    currentValue: any;
    currentValueSource: string;
    submittedValue: any;
    revisedValue: any;
    passed?: boolean;
    ghgRatingFactorSourceId: number;
    ghgRatingFactorSourceComment: string;
    dataType: DataType;
    lookupMappedToProperty: string;
    entityFinderMappedToProperty: string;
    isSelected: boolean;
    vesselProperty: string;
    isVisible: boolean;
    previousValueBeforeOutcome: string;
    isNewProperty: boolean;
}

export interface GhgVerificationRatingFactorClientModel extends BaseGhgVerificationRatingFactorClientModel {
    ghgVerificationRatingFactorId: number;
}

export interface GhgVerificationEexiFactorClientModel extends BaseGhgVerificationRatingFactorClientModel {
    ghgVerificationEexiFactorId: number;
}

export interface GhgVerificationCiiFactorClientModel extends BaseGhgVerificationRatingFactorClientModel {
    ghgVerificationCiiFactorId: number;
}

export interface VesselEnergySavingEquipmentClientModel {
    energySavingEquipmentCategoryId: number;
    energySavingEquipmentId: number;
    implementedDate: string;
}

export interface GhgVerificationEnergySavingEquipmentModel {
    ghgVerificationEnergySavingEquipmentId: number;
    ghgVerificationId: number;
    submittedEnergySavingEquipmentCategoryId: number;
    submittedEnergySavingEquipment: any;
    submittedEnergySavingEquipmentId: number;
    submittedEnergySavingEquipmentName: string;
    submittedImplementedDate: string;
    submittedReductionToMainEnginePower: number;
    submittedReductionToAuxiliaryEnginePower: number;
    submittedMainAvailabilityFactor: number;
    submittedAuxiliaryAvailabilityFactor: number;
    submittedMaker: string;
    submittedModel: string;
    submittedNumberOfUnits: number;
    submittedCO2Reduction: number;
    submittedDescription: string;
    submittedStorageCapacity: number;
    submittedEnergySavingAlternativeFuelId: number;
    revisedEnergySavingEquipmentCategoryId: number;
    revisedEnergySavingEquipmentId: number;
    revisedImplementedDate: string;
    revisedReductionToMainEnginePower: number;
    revisedReductionToAuxiliaryEnginePower: number;
    revisedMainAvailabilityFactor: number;
    revisedAuxiliaryAvailabilityFactor: number;
    revisedMaker: string;
    revisedModel: string;
    revisedNumberOfUnits: number;
    revisedCO2Reduction: number;
    revisedDescription: string;
    revisedStorageCapacity: number;
    revisedEnergySavingAlternativeFuelId: number;
    passed?: boolean;
    ghgRatingFactorSourceId: number;
    ghgRatingFactorSourceComment: string;
    isSelected?: boolean;
    isEplSelected?: boolean;
    showClassificationSociety?: boolean;
    submittedClassificationSocietyId: number;
    revisedClassificationSocietyId: number;
    submittedPaintManufacturerId: number;
    revisedPaintManufacturerId: number;
    submittedPaintTypeId: number;
    revisedPaintTypeId: number;
    submittedProductName: string;
    revisedProductName: string;
    submittedPaintManufacturerName: string;
    revisedPaintManufacturerName: string;
    submittedIsZhip: boolean;
    revisedIsZhip: boolean;
    submittedApprovedZhipProduct: boolean;
    revisedApprovedZhipProduct: boolean;
    submittedHullPreparationStandardId?: number;
    submittedAverageHullRoughness?: number;
    revisedHullPreparationStandardId?: number;
    revisedAverageHullRoughness?: number;
}

export interface ZhipHighPerformancePaint {
    name: string;
    index: number;
    show: boolean;
}

export interface VesselEexiModel {
    vesselEexiId: number;
    ghgVerificationId: number;
    vesselId: number;
    attainedEexi: number;
    requiredEexi: number;
    energyPowerLimitationTypeId: number;
    reducedMainEngineMcr: boolean;
    maximumSpeedAfterEpl: boolean;
    dateOfEplImplementation: boolean;
}

export interface VesselCiiModel {
    vesselCiiId: number;
    ghgVerificationId: number;
    vesselId: number;
    attainedCii: number;
    requiredCii: number;
    ciiRating: number;
    isLatest: number;
}

export interface GetGhgVerificationCiiResponse {
    ghgVerificationCii: GhgVerificationCiiModel;
    vesselCii: VesselCiiModel;
}

export interface GhgVerificationEexiModel {
    ghgVerificationEexiId: number;
    ghgVerificationId: number;
    attainedEexi: number;
    energyPowerLimitationTypeId: number;
    reducedMainEngineMcr: number;
    mainEngineMcrWithoutEpl: number;
    dateOfEplImplementation: string;
    eexiMainSfc: number;
    eexiMainPilotSfc: number;
    speed: number;
}

export interface GhgVerificationCiiModel {
    ghgVerificationCiiId: number;
    ghgVerificationId: number;
    attainedCii: number;
    requiredCii: number;
    ciiRating: string;
    passed?: boolean;
    ghgRatingFactorSourceId: number;
    ghgRatingFactorSourceComment: string;
    ciiRatingYear: number;
    complianceIssueDate: Date;
    ghgVerificationCiiFactorId: number;
}

export interface GhgVerificationStatusClientModel {
    ghgVerificationRatingFactors: GhgVerificationRatingFactorClientModel[];
    ghgVerificationEexiFactors: GhgVerificationEexiFactorClientModel[];
    ghgVerificationCiiFactors: GhgVerificationCiiFactorClientModel[];
    ghgVerificationEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[];
    currentRatingsSummary: any;
    revisedRatingsSummary: any;
    sisterVessels: GhgVerificationSisteVesselModel[];
    ghgVerificationCii: GhgVerificationCiiModel;
    vesselCii: VesselCiiModel;
}

export interface GhgVerificationSisteVesselModel {
    vesselId: number;
    internationalMaritimeOrganizationNumber: string;
    europeanNumberOfIdentification: string;
    vehicleIdentificationNumber: string;
    mainEngineModel: string;
    yardNumber: string;
    isSelected: boolean;
}

export interface GhgVerificationCorrespondenceEmail {
    ghgVerificationCorrespondenceEmailIId?: number;
    ghgVerificationId?: number;
    email: string;
    emailRecipientTypeId: number;
    canEdit?: boolean;

    isFrom: boolean;
    isShipManagerEmail: boolean;
    isFromConfig: boolean;
    isTechnicalManagerEmail: boolean;
    isInspectingCompanyEmail: boolean;
}

export interface GhgVerificationClientModel {
    ghgVerificationId: number;
    vesselId: number;
    ghgVerificationStatusId: number;
    ghgVerificationStatus: string;
    vesselDateOfBuild: string;
    vesselDwt: number;
    vesselAge: number;
    vesselType: string;
    assignedUserId: number;
    vesselName: string;
    ghgVerificationStatusHistory: GhgVerificationStatusHistoryClientModel[];
    assignedTo: string;
    createdBy: string;
    id: number;
    entityType: number;
    identifier: string;
    starRating: number;
    rating: string;
    ratingPlus: boolean;
    safetyRatingType: number;
    internationalMaritimeOrganizationNumber: string;
    vehicleIdentificationNumber: string;
    europeanNumberOfIdentification: string;
    verifyGhg: boolean;
    verifyEnergySavingEquipment: boolean;
    verifyEexi: boolean;
    verifyCii: boolean;
    comments: string;
    date: string;
    createdByEmail: string;
    information: string;
    isClosed: boolean;
    vesselTypeEEDIGroupCode: string;
    isDirectDiesel: boolean;
    isDieselElectric: boolean;
    isShuttleTanker: boolean;
    isSteamTurbine: boolean;
    revisedEmissionRatingPlus: boolean;
    isRevisedGhgRatingVerified: boolean;
    revisedEvdi: number;
    revisedGhgEmissionRating: string;
    revisedSizeScore: number;
    jobId: string;
    jobStatus: string;
    revisedGhgRatingInProgress: boolean;
    documentsCount?: number;
    privateNotesCount?: number;
    correspondenceCount?: number;
    initialEmailsForCorrespondence: SubtaskCorrespondenceEmailModel[];
    ratingBeforeAcceptance: string;
    ratingPlusBeforeAcceptance?: boolean;
    isRatingVerifiedBeforeAcceptance?: boolean;
    ratingAfterAcceptance: string;
    ratingPlusAfterAcceptance?: boolean;
    isRatingVerifiedAfterAcceptance?: boolean;
    vesselYardNumber?: string;
    vesselMainEngineModel?: string;
    submissionSource: string;
    havingOnlyCiiFactors: boolean;
    hasOriginalSpeedPowerCurveUpdated: boolean;
}

export interface GhgVerificationStatusHistoryClientModel {
    statusId: number;
    previousStatusId: number;
    date: string;
    status: string;
    user: string;
    subtaskTypes: any[];
    comment: string;
    fullComment: string;
}

export interface DoesVesselCiiExistRequest {
    vesselId: number;
    ciiRatingYear: number;
}

export interface SaveGhgVerificationRatingFactorsRequest {
    factors: GhgVerificationRatingFactorClientModel[];
    ghgVerificationId: number;
}

export interface ComputeRevisedGhgRequest {
    ghgVerificationId: number;
    ghgVerificationEvdiStatusId: number;
}

export interface SaveGhgVerificationEnergySavingEquipmentsRequest {
    energySavingEquipments: GhgVerificationEnergySavingEquipmentModel[];
    ghgVerificationId: number;
}

export interface SaveGhgVerificationEexiRequest {
    eexiFactors: GhgVerificationEexiFactorClientModel[];
    ghgVerificationId: number;
}

export interface SaveGhgVerificationCiiRequest {
    ciiFactors: GhgVerificationCiiFactorClientModel[];
    ghgVerificationId: number;
}

export interface SaveGhgRatingFactorSourceCommentRequest {
    ghgVerificationId: number;
    ghgVerificationRatingFactorId: number;
    ghgRatingFactorSourceComment: string;
}

export interface SaveGhgEnergySavingEquipmentCommentRequest {
    ghgVerificationId: number;
    ghgVerificationEnergySavingEquipmentId: number;
    ghgRatingFactorSourceComment: string;
}

export interface SaveGhgVerificationEexiCommentRequest {
    ghgVerificationId: number;
    ghgVerificationEexiFactorId: number;
    ghgRatingFactorSourceComment: string;
}

export interface SaveGhgVerificationCiiCommentRequest {
    ghgVerificationId: number;
    ghgVerificationCiiFactorId: number;
    ghgRatingFactorSourceComment: string;
}

export interface SaveGhgVerificationRatingFactorsResponse {
}

export interface ComputeRevisedGhgResponse {
    ghgVerificationId: number;
    revisedEvdi: number;
    revisedSizeScore: number;
    revisedGhgRatingInProgress: boolean;
    revisedGhgEmissionRating: string;
    revisedEmissionRatingPlus: boolean;
    isRevisedGhgRatingVerified: boolean;
    revisedGhgRatingError: string;
    revisedIsEligibleForEVDI: boolean;
    hasRating: boolean;
}

export interface GhgRatingFactorSourceCommentResponse {

}

export interface SaveGhgEnergySavingEquipmentCommentResponse {

}

export interface SaveGhgVerificationEexiCommentResponse {

}

export interface SaveGhgVerificationCiiCommentResponse {

}

export interface SaveGhgVerificationEnergySavingEquipmentsResponse extends BaseValidationResponse {
    ghgVerificationEnergySavingEquipments: GhgVerificationEnergySavingEquipmentModel[];
}

export interface SaveGhgVerificationEexiResponse extends BaseValidationResponse {
    ghgVerificationEexi: GhgVerificationEexiModel;
}

export interface SaveGhgVerificationCiiResponse extends BaseValidationResponse {
    ghgVerificationCii: GhgVerificationCiiModel;
}

export interface GetHasUnacceptableCiiRatingsResponse {
    hasUnacceptableCiiRatings: boolean;
}

export interface EnvironmentalVerificationSettings {
    eexiDwtThreshold: number;
    ghgLowFrictionAntiFoulingSettings: GhgLowFrictionAntiFoulingSettings;
}

export interface GhgLowFrictionAntiFoulingSettings {
    enabled: boolean;
}

export interface GetVesselPendingGhgVerificationResponse {
    ghgVerificationId: number;
}

export interface SaveGhgVerificationRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
    correspondenceEmails: SubtaskCorrespondenceEmailModel[];
    evdiFiles: File[];
    capacityPlanFiles: File[];
    currentClassCertificateFiles: File[];
    mainEngineShopTestFiles: File[];
    auxiliaryEngineShopTestFiles: File[];
    seaTrialReportFiles: File[];
    certifiedDocumentsFiles: File[];
    reliquefactionDocumentationsFiles: File[];
    energySavingEquipmentFiles: File[];
    eexiFiles: File[];
    eplShaPoLiFiles: File[];
    eeCertificateFiles: File[];
    ciiFiles: File[];
    ciiCorrectiveActionPlanFiles: File[];
}

export interface SaveClientGhgVerificationRequest {
    guid: string;
    changes: { [index: string]: UnitOfWorkItem };
    correspondenceEmails: SubtaskCorrespondenceEmailModel[];
    evdiFiles: File[];
    capacityPlanFiles: File[];
    currentClassCertificateFiles: File[];
    mainEngineShopTestFiles: File[];
    auxiliaryEngineShopTestFiles: File[];
    seaTrialReportFiles: File[];
    certifiedDocumentsFiles: File[];
    reliquefactionDocumentationsFiles: File[];
    energySavingEquipmentFiles: File[];
    eexiFiles: File[];
    eplShaPoLiFiles: File[];
    eeCertificateFiles: File[];
    ciiFiles: File[];
    marketingData: { [key: string]: string };
}

export interface GenerateExternalGhgVerificationRequest {
    vesselId: number;
    correspondenceMessage: string;
    correspondenceEmail: SubtaskCorrespondenceEmailModel;
    additionalEmailsToNotify: SubtaskCorrespondenceEmailModel[];
    verifyGhg: boolean;
    verifyEnergySavingEquipment: boolean;
    verifyEexi: boolean;
    verifyCii: boolean;
}

export interface SaveGhgVerificationResponse extends BaseValidationResponse {
    ghgAlreadyExists: boolean;
    generatedId: number;
    existentGhgId: number;
}

export interface GhgRatingModel {
    gHGRatingId: number;
    vesselId: number;
    calculationDate: string;
    inputAndAssumptionsJSON: string;
    intermidiateResultsJSON: string;
    ghgAdditionalMetadataJSON: string;
    evdi: number;
    evdiTo2Dp: number;
    evdiTo3Dp: number;
    evdiLog: number;
    peeringCapacity: string;
    emissionsRating: string;
    emissionsRatingPlus: boolean;
    isVerified: boolean;
    sizeScore: number;
    sizeScoreTo2Dp: number;
    sampleSize: number;
    lowerPeerValue: number;
    upperPeerValue: number;
    hasGhgRating: boolean;
}

export interface SendInviteToDocRequest {
    companyEmailAddress: string;
    emailCorrespondences: string[];
    firstName: string;
    lastName: string;
    jobTitle: string;
    vesselName: string;
    vesselIImo: string;
    verifyOptions: string[];
    companyType: string;
    country: string;
}

export interface SendInviteToDocResponse {
    isEmailWaitingApproval: boolean;
    isEmailSent: boolean;
}

export interface EmailValidationRequest {
    contactEmail: string;
}

export enum EnergySavingEquipmentEnum {
    HybridAuxiliaryPowerGeneration = 1,
    EnginePowerLimitation = 2,
    EngineTuning = 3,
    SolarPower = 4,
    WasteHeatRecovery = 5,
    FlettnerRotors = 6,
    EngineNozzleOptimisation = 7,
    BatteryPoweredEngine = 8,
    WAPS = 9,
    HullCleaning = 10,
    AntiFoullingHullCoating = 11,
    PropellerSurfacePolish = 12,
    WeatherRoutingAndVoyageOptimisation = 13,
    CounterRotatingPropellers = 14,
    AdvancedPropellerBladeDesign = 15,
    PropellerBossCapFin = 16,
    PropellerDuctOrNozzle = 17,
    PropellerTipWinglets = 18,
    PropellerHullInteraction = 19,
    RudderOptimisation = 20,
    HullAirLubrication = 21,
    HullDuctOrFin = 22,
    AlternativeFuel = 24,
    CarbonCaptureSystem = 25
}

export enum EnergyPowerLimitationTypeEnum {
    EPL = 1,
    ShaPoLi = 2
}

export enum GhgVerificationStatusEnum {
    PendingShipManager = 1,
    Pending = 2,
    InProgress = 3,
    AwaitingResponse = 4,
    PendingReview = 5,
    Accepted = 6,
    Rejected = 7,
    Cancelled = 8,
    GhgVerificationReassignment = 9,
    Expired = 10
}

export class GhgVerificationActivitiesColumns {
    public static getColumns(rightShipUser: boolean,): DataGridColumn[] {
        return [

            <DataGridColumn>
            {
                dataType: DataType.CustomTemplate,
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href.bind="getItemRoute(item.ghgVerificationId)" mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                    '<svg class="rs-badge" viewBox="0 0 30 30" if.bind="item.isRightShipMember && isRightShipUser">' +
                    '<g>' +
                    '<path fill="#003745" d="M2,0h26c1.1,0,2,0.9,2,2v26c0,1.1-0.9,2-2,2H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '<path fill="#FF5F1B" d="M2,0h1.5c0.5,0,1,0.2,1.4,0.6l15.5,14.8l0,0L4.9,29.5C4.5,29.8,4,30,3.5,30H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '</g>' +
                    '</svg>' +
                    '</div>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span></div>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span></div>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>',
                name: "vesselName",
                title: "Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    getItemRoute: (id) => `/environmental-review-request/${id}?source=activities`,
                    itemClick: () => {
                        HistoryHelper.addState(TasksConstants.historyTaskTabKey, TasksConstants.GHGListName);

                        return true;
                    },
                    isRightShipUser: rightShipUser
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 6,
                dataType: DataType.DateTime,
                name: 'submittedDate',
                title: 'Submitted Date',
                isSortable: true,
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY HH:mm')
                    : '',
            },
            <RightshipDataGridColumn>{
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                name: 'information',
                title: 'Information',
                ellipsis: 75,
                isSortable: false,
                disableSorting: true,
            },
            <DataGridColumn>{
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'requesterCustomer',
                title: 'Customer/Environmental ID',
                value:
                    '<div class="reviewer">' +
                    '<span class="name" if.bind="item.requester">${item.requesterCustomer} - ${item.requester}' +
                    '</span>' +
                    '</span>' +
                    '<span class="type" if.bind="item.ghgVerificationId">Environmental ID #${item.ghgVerificationId}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'reviewer',
                title: 'Reviewer',
                value:
                    '<div class="reviewer" if.bind="item.reviewer">' +
                    '<span class="name">${item.reviewer}</span>' +
                    '<span class="type" if.bind="item.reviewerType">${item.reviewerType}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 6,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'status',
                title: 'Status',
                value:
                    '<div class="status-badge-value" if.bind="item.status">' +
                    '<div class="row nowrap align-items-middle">' +
                    '<div class="status-badge-dot"></div>' +
                    '<span>${item.status}</span>' +
                    '<div class="info" if.bind="item.awaitingResponseStatusReason && item.status.toLowerCase() != \'unacceptable\' && item.status.toLowerCase() != \'acceptable\'">' +
                    '<i class="icon-info material-icons">info_outline</i>' +

                    //Tooltip
                    '<div class="status-tooltip">' +
                    '<div class="wrap-tooltip">' +
                    '<p class="desc">${item.awaitingResponseStatusReason}</p>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '<span class="status-date">${getDateToString(item.statusDate)}</span>' +
                    '</div>',
                isSortable: true,
                getCellClass: (item: { status: string }) => {
                    return (item && item.status) ? `status-badge ${item.status.split(' ').join('-').toLowerCase()}` : '';
                },
                parameter: {
                    item: null,
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                }
            }
        ];
    }

    public static getDateToString(date: Date | string): string {
        if (!date)
            return 'N/A'

        let val: any = date;

        if (typeof date == 'string')
            val = new Date(date);

        if (val.getUTCMonth() == 0 && val.getUTCDay() == 1 && val.getUTCFullYear() == 1900)
            return 'N/A';

        return date ? moment(date).fromNow() : null;
    }
}

export const GhgRatingGroups =
{
    EVDI: "EVDI",
    EEXI: "EEXI",
    EVDIAndGHGVerificationStatus: "EVDI and Environmental review status",
    ClassNotation: "Class notation",
    Tonnages: "Tonnages",
    Dimensions: "Dimensions",
    Capacity: "Capacity",
    Gear: "Gear",
    EnginePerformance: "Engine performance",
    ReliquefactionSystem: "Additional equipment : Re-liquefaction system",
    Compressors: "Additional equipment : Compressors",
    Speed: "Speed",
}

export interface SubmitGhgRatingChangeRequest {
    firstName: string;
    lastName: string;
    jobTitle: string;
    companyName: string;
    companyType: string;
    country: string;
    contactEmail: string;
    agreeToReceiveMarketingCommunications: boolean;
    agreeToTermsAndConditions: boolean;
}

export interface SubmitGhgRatingChangeResponse {
    validationResult: ValidationResult;
}

export interface GetEstimatedCiiResponse {
    vesselImo: string;
    historicalCiiRatings: HistoricalCiiRating[];
}

export interface HistoricalCiiRating {
    cumulativeCii: CumulativeCiiModel;
    yearWeekNo: string;
    nonCumulativeCii: CumulativeCiiModel;
    year: string;
    weekNo: string;
}

export interface CumulativeCiiModel {
    ciiRating: string;
    ciiInGco2PerTonneNm: string;
    periodStart: string;
    averageSpeedInKn: string;
    periodEnd: string;
    mainEngineFuelConsumptionInMt: string;
    fuelConsumptionInMt: string;
    distanceInNm: string;
    auxEngineFuelConsumptionInMt: string;
}

export interface GetPaintTypesResponse {
    paintTypes: PaintType[];
}

export interface GetHullPreparationStandardResponse {
    hullPreparationStandards: HullPreparationStandardClientModel[];
}

export interface GetPaintManufacturersResponse {
    paintManufacturers: PaintManufacturerClientModel[];
}

export interface HullPreparationStandardClientModel {
    hullPreparationStandardId: number;
    name: string;
}

export interface PaintManufacturerClientModel {
    id: number;
    name: string;
    productNames: string[];
}

export interface PaintType {
    id: number;
    value: string;
}

export enum EnergySavingEquipmentCategoryEnum {
    Machinery = 1,
    Operations = 2,
    Hull = 3
}

export enum ZHIPHighPerformancePaintEnum {
    Others = 3,
}

export interface GetClassificationSocietyResponse {
    classificationSocieties: ClassificationSocietyClientModel[];
}

export interface ClassificationSocietyClientModel {
    classificationSocietyId: number;
    name: string;
}